import { formatPhoneNumber, cleanPhoneNumber } from '@/features/invitee/utils/inviteeFormatters'
import { resolveStateToCode, type State } from '@/lib/api/statesService'
import { MAILING_ADDRESS_FORM_FIELDS } from '../constants'
import {
    isMobileValidAngularStyle,
    isZipcodeValidAngularStyle,
    resolvePmcInviteParity,
    stripPhoneDigitsForApi,
} from './companyValidators'
import type { CompanyAddFormValues, CompanyEditData } from '../types'

export {
    ADDRESS_VERIFY_HTML,
    ERROR_ADDRESS_VERIFY,
    MAILING_ADDRESS_FORM_FIELDS,
    PHONE_FORMAT_ERROR,
    ZIPCODE_FORMAT_ERROR,
} from '../constants'

/** Angular `mailingAddress()` — reset mailing fields when "Different Mailing Address?" is unchecked. */
export function clearMailingAddressFormFields(
    setValue: (name: keyof CompanyAddFormValues, value: string) => void,
    clearErrors: (names?: keyof CompanyAddFormValues | (keyof CompanyAddFormValues)[]) => void,
): void {
    for (const field of MAILING_ADDRESS_FORM_FIELDS) {
        setValue(field, '')
    }
    clearErrors([...MAILING_ADDRESS_FORM_FIELDS])
}

/** Matches Angular `zipcodeValidator`: raw string length 5 or 9 (hyphenated ZIP+4 length 10 fails). */
export function isValidPhysicalZipcode(value: string | undefined): boolean {
    const raw = String(value ?? '')
    if (!raw) return false
    return isZipcodeValidAngularStyle(raw)
}

/** Matches Angular `mobileValidator` (optional empty when not required). */
export function isValidPhoneForSave(
    value: string | undefined,
    { required }: { required: boolean },
): boolean {
    const raw = value ?? ''
    if (!raw.trim()) return !required
    return isMobileValidAngularStyle(raw)
}

export function isLegalNameVerified(val: unknown): boolean {
    if (val === true || val === 1 || val === '1') return true
    if (typeof val === 'string') {
        const s = val.trim().toUpperCase()
        return s === 'YES' || s === '1' || s === 'TRUE'
    }
    return false
}

export function normalizeLegalVerifiedValue(val: unknown): string {
    if (isLegalNameVerified(val)) return 'YES'
    if (val === false || val === 0 || val === '0') return 'NO'
    if (typeof val === 'string') {
        const s = val.trim().toUpperCase()
        if (s === 'NO' || s === '0' || s === 'FALSE') return 'NO'
    }
    return ''
}

/** Align with Angular company-edit: patchValue(response) + select options use state codes from /states. */
export function buildCompanyInfoFormValues(
    company: CompanyEditData,
    states: State[],
): CompanyAddFormValues {
    const physicalStateRaw =
        company?.company_state ?? company?.state ?? company?.company_state_code ?? ''
    const mailingStateRaw =
        company?.mailing_company_state ?? company?.mailing_state ?? company?.mailing_state_code ?? ''

    return {
        company_name: company?.company_name || '',
        legal_name: String(company?.legal_name ?? company?.company_legal_name ?? ''),
        legal_verified:
            company?.legal_verified != null && String(company.legal_verified).trim() !== ''
                ? String(company.legal_verified)
                : '',
        company_alias_name: company?.company_alias_name || '',
        office_phone: formatPhoneNumber(cleanPhoneNumber(company?.company_office_phone || '')),
        contact_name: company?.contact_name || '',
        contact_mobile: formatPhoneNumber(cleanPhoneNumber(company?.company_phone || '')),
        address1: company?.company_address || '',
        address2: company?.company_address2 || '',
        city: company?.company_city || '',
        state: resolveStateToCode(physicalStateRaw, states),
        zipcode: company?.company_zipcode || '',
        listed: String(company?.listed || '1'),
        latitude: company?.latitude ? Number(company.latitude) : undefined,
        longitude: company?.longitude ? Number(company.longitude) : undefined,
        federal_id: company?.federal_id || '',
        is_mailing_address:
            company?.is_mailing_address === '1' ||
            company?.is_mailing_address === true ||
            company?.is_mailing_address === 1,
        mailing_address1: company?.mailing_company_address || '',
        mailing_address2: company?.mailing_company_address2 || '',
        mailing_city: company?.mailing_company_city || '',
        mailing_state: resolveStateToCode(mailingStateRaw, states),
        mailing_zipcode: company?.mailing_company_zipcode || '',
        email_frequency:
            company?.email_frequency != null && String(company.email_frequency).trim() !== ''
                ? String(company.email_frequency).trim()
                : 'MONTHLY',
    }
}

export function buildCompanyInfoSavePayload(
    data: CompanyAddFormValues,
    company: CompanyEditData | undefined,
): Record<string, unknown> {
    const isMailing = Boolean(data.is_mailing_address)
    const rawPayload: Record<string, unknown> = {
        company_name: data.company_name,
        legal_name: data.legal_name,
        legal_verified: data.legal_verified,
        company_alias_name: data.company_alias_name,
        company_office_phone: stripPhoneDigitsForApi(data.office_phone),
        contact_name: data.contact_name,
        company_phone: stripPhoneDigitsForApi(data.contact_mobile),
        listed: data.listed,
        company_address: data.address1,
        company_address2: data.address2,
        company_city: data.city,
        company_state: data.state,
        company_zipcode: data.zipcode,
        latitude: data.latitude,
        longitude: data.longitude,
        federal_id: data.federal_id,
        mailing_company_address: isMailing ? data.mailing_address1 : '',
        mailing_company_address2: isMailing ? data.mailing_address2 : '',
        mailing_company_city: isMailing ? data.mailing_city : '',
        mailing_company_state: isMailing ? data.mailing_state : '',
        mailing_company_zipcode: isMailing ? data.mailing_zipcode : '',
        company_managed: data.company_managed,
    }

    const isCurrentlyVerified = isLegalNameVerified(company?.legal_verified)
    const formVerified = isLegalNameVerified(data.legal_verified)

    if (isCurrentlyVerified) {
        if (data.legal_verified === 'NO') {
            rawPayload.legal_verified = 'NO'
        }
        // If company was ALREADY verified and form is still verified ('YES'), omit legal_verified
        // so backend update logic doesn't create duplicate "Legal Name Verified: YES" notes on every save.
    } else {
        if (formVerified) {
            rawPayload.legal_verified = 'YES'
        } else if (data.legal_verified === 'NO') {
            rawPayload.legal_verified = 'NO'
        }
    }

    if (company?.c_aln_ref_id != null && String(company.c_aln_ref_id).trim() !== '') {
        rawPayload.c_aln_ref_id = company.c_aln_ref_id
    }
    if (company?.website_url != null && String(company.website_url).trim() !== '') {
        rawPayload.website_url = company.website_url
    }
    if (!rawPayload.federal_id) {
        rawPayload.federal_id = ''
    }
    rawPayload.is_mailing_address = data.is_mailing_address ? 1 : 0
    rawPayload.check_exist = true
    rawPayload.pmc_invite = resolvePmcInviteParity(
        company as Record<string, unknown> | undefined,
    )
    return rawPayload
}

export function resolveCompanyInfoId(
    companyIdProp: string | number | undefined,
    company: CompanyEditData | undefined,
): string | number | undefined {
    return companyIdProp ?? company?.company_id ?? company?.id ?? company?.fk_company_id
}

export function isPhysicalAddressField(name: string | undefined): boolean {
    return (
        name === 'address1' ||
        name === 'address2' ||
        name === 'city' ||
        name === 'state' ||
        name === 'zipcode'
    )
}

export function buildFullPhysicalAddress(data: CompanyAddFormValues): string {
    let fullAddress = data.address1
    if (data.address2) fullAddress += ' ' + data.address2
    fullAddress += ` ${data.city} ${data.state} ${data.zipcode}`
    return fullAddress
}
